/**
 * DayTimeline — Apple-Calendar-style vertical time strip for the Planner Day View.
 *
 * Renders the day from 5 AM to 11 PM at 64 px / hour with:
 *   • Hour grid with half-hour ticks (low-contrast)
 *   • Calendar events (Google / Microsoft / iCal) — read-only, color-coded
 *   • Task blocks — interactive: drag-to-move, edge-resize, checkbox-complete
 *   • "Now" indicator (red pill + line) on the current local time
 *   • All-day calendar events row pinned above the strip
 *   • Drag-drop target for tasks dragged from the list (HTML5 DnD)
 *
 * Behavioral notes:
 *   • Tasks with no `time` field never render here (list-only).
 *   • Tasks with `time` OUTSIDE the visible window are also filtered out (they
 *     remain in the list view). This avoids silent data mutation when a drag
 *     starts from a clamped origin.
 *   • Tasks with `time` but no `duration_minutes` render as 60 min by default.
 *   • Drag/resize commits state ONLY on pointer release — no re-render storm.
 *   • Overlapping blocks are laid out via a simple lane-assignment algorithm.
 *   • Multi-day events (and same-day events crossing the 5 AM / 11 PM window)
 *     are clipped with ↑ / ↓ arrows.
 *   • Dropping a task from the list also updates its `date` to selectedDate
 *     (handles overdue-rolled-up tasks correctly).
 *   • Below `lg` (1024 px) this component is unmounted by the parent (DayView).
 *
 * Coordinate system:
 *   All absolute children are positioned relative to the inner `.relative`
 *   div using `topPxForMin(min)` directly. NO padding offsets — past versions
 *   added `+8` in some places and not others, causing 8 px misalignment
 *   between tasks and events. Don't reintroduce that.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronsUpDown, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';
import { formatTime12h } from '../../../utils/timeFormat';

// ── Layout constants ──────────────────────────────────────────────────────────
const HOUR_START = 5;       // 5 AM
const HOUR_END = 23;        // 11 PM (last labeled row at 23:00; total span 18 h)
const HOURS_VISIBLE = HOUR_END - HOUR_START; // 18
const PX_PER_HOUR = 64;
const PX_PER_MIN = PX_PER_HOUR / 60;
const TOTAL_PX = HOURS_VISIBLE * PX_PER_HOUR; // 1152 px
const STRIP_BOTTOM_BUFFER = 16; // empty space below the 11 PM line
const SNAP_MINUTES = 15;
const DEFAULT_DURATION = 60;
const MIN_DURATION = 15;
const LABEL_COL_PX = 56;    // left rail width for hour labels

// ── Helpers ───────────────────────────────────────────────────────────────────
const minutesOfDayFromISO = (iso) => {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
};

const timeStringToMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
};

const minutesToTimeString = (mins) => {
  const m = ((mins % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

const snap = (mins) => Math.round(mins / SNAP_MINUTES) * SNAP_MINUTES;

// Y-pixel offset (within the strip) for a given absolute minute-of-day.
const topPxForMin = (min) => (min - HOUR_START * 60) * PX_PER_MIN;

// Project a Y position (within the scroll container) back to minute-of-day, snapped.
const yPxToSnappedMin = (yPx) => {
  const raw = HOUR_START * 60 + yPx / PX_PER_MIN;
  return Math.max(HOUR_START * 60, Math.min(HOUR_END * 60 - MIN_DURATION, snap(raw)));
};

// ── Lane-assignment for overlapping items ─────────────────────────────────────
// Returns an array of { item, lane, totalLanes } for the input list.
// Items must have numeric `startMin` and `endMin` fields.
const assignLanes = (items) => {
  if (!items.length) return [];
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const lanes = []; // each entry holds the running endMin of that lane
  const placed = sorted.map((it) => {
    let lane = lanes.findIndex((endMin) => endMin <= it.startMin);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(it.endMin);
    } else {
      lanes[lane] = it.endMin;
    }
    return { item: it, lane };
  });
  // Compute totalLanes per cluster (events that transitively overlap).
  const events = [];
  sorted.forEach((it, idx) => {
    events.push({ t: it.startMin, type: 'start', idx });
    events.push({ t: it.endMin, type: 'end', idx });
  });
  events.sort((a, b) => a.t - b.t || (a.type === 'end' ? -1 : 1));
  const active = new Set();
  let cluster = [];
  const clusterId = new Array(sorted.length).fill(-1);
  let nextCluster = 0;
  for (const ev of events) {
    if (ev.type === 'start') {
      active.add(ev.idx);
      cluster.push(ev.idx);
    } else {
      active.delete(ev.idx);
      if (active.size === 0) {
        cluster.forEach((i) => { clusterId[i] = nextCluster; });
        nextCluster++;
        cluster = [];
      }
    }
  }
  const clusterMaxLane = {};
  placed.forEach(({ lane }, i) => {
    const cid = clusterId[i];
    clusterMaxLane[cid] = Math.max(clusterMaxLane[cid] ?? 0, lane);
  });
  return placed.map(({ item, lane }, i) => ({
    item,
    lane,
    totalLanes: (clusterMaxLane[clusterId[i]] ?? 0) + 1,
  }));
};

// ── Hour grid (memoized) ──────────────────────────────────────────────────────
const HourGrid = React.memo(function HourGrid() {
  const rows = [];
  for (let h = HOUR_START; h < HOUR_END; h++) {
    const top = topPxForMin(h * 60);
    const hour12 = ((h + 11) % 12) + 1;
    const ampm = h < 12 ? 'AM' : 'PM';
    const label = h === 12 ? 'Noon' : `${hour12}`;
    rows.push(
      <React.Fragment key={h}>
        {/* hour line */}
        <div
          data-ui="tl-hour"
          className="absolute left-0 right-0 border-t border-slate-200/80 dark:border-white/[0.06]"
          style={{ top }}
        />
        {/* half-hour tick */}
        <div
          data-ui="tl-half"
          className="absolute right-0 border-t border-dashed border-slate-100 dark:border-white/[0.03]"
          style={{ top: top + PX_PER_HOUR / 2, left: LABEL_COL_PX }}
        />
        {/* label — placed BELOW the line so the 5 AM label stays visible */}
        <div
          data-ui="tl-label"
          className="absolute left-0 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 select-none pl-1.5"
          style={{ top: top + 2, width: LABEL_COL_PX - 4, textAlign: 'right' }}
        >
          <span className="font-semibold">{label}</span>
          {h !== 12 && <span data-ui="tl-ampm" className="ml-1 opacity-60">{ampm}</span>}
        </div>
      </React.Fragment>
    );
  }
  // bottom border at 11 PM
  rows.push(
    <div
      key="end"
      data-ui="tl-hour"
      className="absolute left-0 right-0 border-t border-slate-200/80 dark:border-white/[0.06]"
      style={{ top: TOTAL_PX }}
    />
  );
  return <>{rows}</>;
});

// ── Now indicator ─────────────────────────────────────────────────────────────
function NowIndicator({ now, isToday }) {
  if (!isToday) return null;
  const mins = now.getHours() * 60 + now.getMinutes();
  if (mins < HOUR_START * 60 || mins > HOUR_END * 60) return null;
  const top = topPxForMin(mins);
  const label = format(now, 'h:mm');
  // Pill is ~16px tall; nudge up by 8 to center it on the line.
  return (
    <div
      className="absolute left-0 right-0 pointer-events-none z-30"
      style={{ top: top - 8 }}
      data-testid="timeline-now-indicator"
    >
      <div className="flex items-center">
        <div className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none shadow-sm">
          {label}
        </div>
        <div className="flex-1 h-px bg-red-500" />
      </div>
    </div>
  );
}

// ── Helper to build column-fraction CSS for lane layout ──
const laneStyle = (lane, totalLanes) => ({
  left: `calc(${LABEL_COL_PX}px + 4px + ${(lane * 100) / totalLanes}% - ${(lane * (LABEL_COL_PX + 8)) / totalLanes}px)`,
  width: `calc(${100 / totalLanes}% - ${(LABEL_COL_PX + 8) / totalLanes}px - 4px)`,
});

// ── Calendar event block (read-only) ──────────────────────────────────────────
function EventBlock({ event, top, height, lane, totalLanes, clippedTop, clippedBottom }) {
  const color = event.color || '#3b82f6';
  return (
    <a
      href={event.html_link || undefined}
      target={event.html_link ? '_blank' : undefined}
      rel="noopener noreferrer"
      data-ui="tl-event"
      className="absolute rounded-md overflow-hidden text-[11px] leading-tight shadow-sm hover:shadow-md transition-shadow group"
      style={{
        top,
        height: Math.max(height, 18),
        ...laneStyle(lane, totalLanes),
        backgroundColor: `${color}22`,
        borderLeft: `3px solid ${color}`,
        color: 'inherit',
        zIndex: 10,
      }}
      data-testid={`timeline-event-${event.id}`}
      title={event.title}
    >
      {clippedTop && <ChevronUp className="absolute top-0 right-1 w-3 h-3 opacity-70" />}
      <div className="px-2 py-1 truncate font-medium" style={{ color }}>{event.title}</div>
      {height > 32 && (
        <div className="px-2 pb-1 text-[10px] opacity-80 truncate flex items-center gap-1" style={{ color }}>
          {!clippedTop && !clippedBottom && (
            <>{format(new Date(event.start), 'h:mm a')} – {format(new Date(event.end), 'h:mm a')}</>
          )}
          {(clippedTop || clippedBottom) && <span>continues</span>}
          {event.html_link && <ExternalLink className="w-2.5 h-2.5 opacity-60 ml-auto group-hover:opacity-100" />}
        </div>
      )}
      {clippedBottom && <ChevronDown className="absolute bottom-0 right-1 w-3 h-3 opacity-70" />}
    </a>
  );
}

// ── Task block (interactive) ──────────────────────────────────────────────────
function TaskBlock({
  task,
  startMin,
  durationMin,
  lane,
  totalLanes,
  onToggleComplete,
  onEdit,
  onCommit,    // (taskId, newTime, newDuration) — fired on pointer up
}) {
  const top = topPxForMin(startMin);
  const height = Math.max(durationMin * PX_PER_MIN, 24);
  const blockRef = useRef(null);

  // Live drag state — kept in ref to avoid re-renders during pointer-move.
  const dragRef = useRef(null);
  // AbortController owns the active pointer listeners. Cleanly cancels on
  // pointerup, on component unmount, or if a second drag starts.
  const dragAbortRef = useRef(null);

  const setPreviewStyle = (yTop, h) => {
    if (!blockRef.current) return;
    blockRef.current.style.top = `${yTop}px`;
    blockRef.current.style.height = `${h}px`;
  };

  const beginDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    // If a stale drag is still attached (defensive), abort it first.
    dragAbortRef.current?.abort();
    const controller = new AbortController();
    dragAbortRef.current = controller;
    dragRef.current = {
      mode,
      startClientY: e.clientY,
      origStartMin: startMin,
      origDurationMin: durationMin,
      moved: false,
    };
    document.body.style.cursor = mode.startsWith('resize') ? 'ns-resize' : 'grabbing';

    const onMove = (ev) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaPx = ev.clientY - drag.startClientY;
      const deltaMinRaw = deltaPx / PX_PER_MIN;
      if (Math.abs(deltaPx) > 4) drag.moved = true;

      if (drag.mode === 'move') {
        const newStart = Math.max(
          HOUR_START * 60,
          Math.min(HOUR_END * 60 - drag.origDurationMin, snap(drag.origStartMin + deltaMinRaw))
        );
        setPreviewStyle(topPxForMin(newStart), Math.max(drag.origDurationMin * PX_PER_MIN, 24));
        drag.previewStart = newStart;
        drag.previewDuration = drag.origDurationMin;
      } else if (drag.mode === 'resize-bottom') {
        const newEnd = Math.max(
          drag.origStartMin + MIN_DURATION,
          Math.min(HOUR_END * 60, snap(drag.origStartMin + drag.origDurationMin + deltaMinRaw))
        );
        const newDur = newEnd - drag.origStartMin;
        setPreviewStyle(topPxForMin(drag.origStartMin), Math.max(newDur * PX_PER_MIN, 24));
        drag.previewStart = drag.origStartMin;
        drag.previewDuration = newDur;
      } else if (drag.mode === 'resize-top') {
        const origEnd = drag.origStartMin + drag.origDurationMin;
        const newStart = Math.max(
          HOUR_START * 60,
          Math.min(origEnd - MIN_DURATION, snap(drag.origStartMin + deltaMinRaw))
        );
        const newDur = origEnd - newStart;
        setPreviewStyle(topPxForMin(newStart), Math.max(newDur * PX_PER_MIN, 24));
        drag.previewStart = newStart;
        drag.previewDuration = newDur;
      }
    };

    const onUp = () => {
      controller.abort();
      document.body.style.cursor = '';
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;

      if (!drag.moved) {
        onEdit(task);
        return;
      }
      const newStart = drag.previewStart ?? drag.origStartMin;
      const newDur = drag.previewDuration ?? drag.origDurationMin;
      if (newStart === drag.origStartMin && newDur === drag.origDurationMin) return;
      onCommit(task.id, minutesToTimeString(newStart), newDur);
    };

    window.addEventListener('pointermove', onMove, { signal: controller.signal });
    window.addEventListener('pointerup', onUp, { signal: controller.signal });
  };

  // Defensive cleanup on unmount.
  useEffect(() => () => {
    dragAbortRef.current?.abort();
    document.body.style.cursor = '';
  }, []);

  const isOverdue = task.isOverdue && !task.completed;

  return (
    <div
      ref={blockRef}
      data-ui="tl-task"
      className={`absolute rounded-md overflow-hidden shadow-sm select-none transition-shadow hover:shadow-md ${task.completed ? 'st-done' : ''} ${
        task.completed
          ? 'bg-slate-100/80 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10'
          : isOverdue
            ? 'bg-amber-100 dark:bg-amber-500/20 border border-amber-300 dark:border-amber-500/40'
            : 'bg-indigo-100 dark:bg-indigo-500/25 border border-indigo-300 dark:border-indigo-500/40'
      }`}
      style={{
        top,
        height,
        ...laneStyle(lane, totalLanes),
        zIndex: 20,
        cursor: 'grab',
      }}
      onPointerDown={(e) => {
        if (e.target.closest('[data-resize-handle]') || e.target.closest('[data-checkbox]')) return;
        if (e.button !== 0) return;
        beginDrag('move', e);
      }}
      data-testid={`timeline-task-${task.id}`}
      title={task.title}
    >
      {/* Top resize handle */}
      <div
        data-resize-handle="top"
        className="absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize group/handle z-10"
        onPointerDown={(e) => beginDrag('resize-top', e)}
      >
        <ChevronsUpDown className="absolute top-[-2px] right-1 w-3 h-3 text-slate-500 dark:text-slate-300 opacity-0 group-hover/handle:opacity-100 transition-opacity" />
      </div>
      {/* Bottom resize handle */}
      <div
        data-resize-handle="bottom"
        className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize group/handle z-10"
        onPointerDown={(e) => beginDrag('resize-bottom', e)}
      >
        <ChevronsUpDown className="absolute bottom-[-2px] right-1 w-3 h-3 text-slate-500 dark:text-slate-300 opacity-0 group-hover/handle:opacity-100 transition-opacity" />
      </div>

      <div className="flex items-start gap-1.5 px-1.5 py-1">
        <div data-checkbox onPointerDown={(e) => e.stopPropagation()} className="pt-0.5">
          <Checkbox
            checked={task.completed}
            onCheckedChange={() => onToggleComplete(task.id, task.completed)}
            className="h-3.5 w-3.5"
            data-testid={`timeline-task-check-${task.id}`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] font-medium leading-tight truncate ${
            task.completed
              ? 'line-through text-slate-400 dark:text-slate-500'
              : isOverdue
                ? 'text-amber-900 dark:text-amber-100'
                : 'text-indigo-900 dark:text-indigo-100'
          }`}>
            {task.title}
          </div>
          {height > 32 && (
            <div className={`text-[10px] truncate ${
              isOverdue ? 'text-amber-700 dark:text-amber-300' : 'text-indigo-700 dark:text-indigo-300'
            } ${task.completed ? 'opacity-60' : ''}`}>
              {formatTime12h(minutesToTimeString(startMin))} – {formatTime12h(minutesToTimeString(startMin + durationMin))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function DayTimeline({
  selectedDate,
  tasks,         // already filtered to selectedDate by parent (includes overdue rolled forward)
  events,        // already filtered to selectedDate by parent
  onToggleComplete,
  onEditTask,
  onCommitTaskTime, // (taskId, newTimeHHMM, newDurationMin, newDate?) — persistence handler
}) {
  const [now, setNow] = useState(() => new Date());
  const scrollRef = useRef(null);
  const [dragOver, setDragOver] = useState(null); // { yPx, snappedMin } | null

  const isToday = isSameDay(selectedDate, now);
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

  // Tick "now" once per minute.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Auto-scroll on mount / date change.
  useEffect(() => {
    if (!scrollRef.current) return;
    let targetMin;
    if (isToday) {
      const cur = now.getHours() * 60 + now.getMinutes();
      targetMin = (cur >= HOUR_START * 60 && cur <= HOUR_END * 60) ? cur : 8 * 60;
    } else {
      targetMin = 8 * 60;
    }
    const top = Math.max(0, topPxForMin(targetMin) - scrollRef.current.clientHeight / 3);
    scrollRef.current.scrollTop = top;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Split events: all-day vs timed.
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay = [];
    const timed = [];
    for (const ev of events) {
      if (ev.all_day) allDay.push(ev);
      else timed.push(ev);
    }
    return { allDayEvents: allDay, timedEvents: timed };
  }, [events]);

  // Project events to visible-window minutes (clipping for multi-day OR for
  // same-day events that extend outside the 5 AM – 11 PM window).
  const visibleEventLayouts = useMemo(() => {
    const dayStart = new Date(selectedDate); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(selectedDate); dayEnd.setHours(23, 59, 59, 999);
    const items = timedEvents.map((ev) => {
      const start = new Date(ev.start);
      const end = new Date(ev.end);
      const crossesDayTop = start < dayStart;
      const crossesDayBottom = end > dayEnd;
      const rawStartMin = crossesDayTop ? HOUR_START * 60 : minutesOfDayFromISO(ev.start);
      const rawEndMin = crossesDayBottom ? HOUR_END * 60 : minutesOfDayFromISO(ev.end);
      // Clip indicators fire for either cross-day OR cross-window same-day extension.
      const clippedTop = crossesDayTop || rawStartMin < HOUR_START * 60;
      const clippedBottom = crossesDayBottom || rawEndMin > HOUR_END * 60;
      const startMin = Math.max(HOUR_START * 60, rawStartMin);
      const endMin = Math.min(HOUR_END * 60, Math.max(rawEndMin, startMin + 15));
      return {
        kind: 'event',
        event: ev,
        startMin,
        endMin,
        clippedTop,
        clippedBottom,
      };
    }).filter((it) => it.endMin > it.startMin && it.startMin < HOUR_END * 60 && it.endMin > HOUR_START * 60);
    return assignLanes(items);
  }, [timedEvents, selectedDate]);

  // Project tasks (only those with `time` AND inside the visible window).
  // Tasks outside the window stay in the list view — never silently clamp.
  const visibleTaskLayouts = useMemo(() => {
    const items = tasks
      .filter((t) => {
        if (!t.time) return false;
        const m = timeStringToMinutes(t.time);
        return m !== null && m >= HOUR_START * 60 && m < HOUR_END * 60;
      })
      .map((t) => {
        const startMin = timeStringToMinutes(t.time);
        const dur = t.duration_minutes || DEFAULT_DURATION;
        const endMin = Math.min(HOUR_END * 60, startMin + dur);
        return {
          kind: 'task',
          task: t,
          startMin,
          durationMin: endMin - startMin,
          endMin,
        };
      });
    return assignLanes(items);
  }, [tasks]);

  // ── Drag-drop from external task list ──
  // yPx is measured directly inside the inner positioned div — no padding offset.
  const eventToYPx = (e) => {
    const rect = scrollRef.current.getBoundingClientRect();
    return (e.clientY - rect.top) + scrollRef.current.scrollTop;
  };

  const onStripDragOver = useCallback((e) => {
    // Some browsers (e.g. Firefox) omit `text/plain` from dragover for security.
    // Always accept the drop; we'll validate on drop.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const yPx = eventToYPx(e);
    const snapped = yPxToSnappedMin(yPx);
    setDragOver({ yPx: topPxForMin(snapped), snappedMin: snapped });
  }, []);

  const onStripDragLeave = useCallback((e) => {
    // Only trust relatedTarget when present (Chromium/Firefox). WebKit leaves
    // it null and misreports drag coords when scrolled — there the window
    // dragend/drop cleanup below handles clearing.
    if (e.relatedTarget && !scrollRef.current?.contains(e.relatedTarget)) setDragOver(null);
  }, []);

  useEffect(() => {
    const clear = () => setDragOver(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => {
      window.removeEventListener('dragend', clear);
      window.removeEventListener('drop', clear);
    };
  }, []);

  const onStripDrop = useCallback((e) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    setDragOver(null);
    if (!taskId) return;
    const yPx = eventToYPx(e);
    const snapped = yPxToSnappedMin(yPx);
    const existing = tasks.find((t) => t.id === taskId);
    if (!existing) return;
    const dur = existing.duration_minutes || DEFAULT_DURATION;
    const clampedStart = Math.min(snapped, HOUR_END * 60 - dur);
    // Pass selectedDateStr so the commit also moves the task's date — fixes
    // overdue-task-rolled-up-to-today drops, and is the correct semantic for
    // any drag-from-list operation onto a specific day's timeline.
    onCommitTaskTime(taskId, minutesToTimeString(clampedStart), dur, selectedDateStr);
  }, [tasks, onCommitTaskTime, selectedDateStr]);

  // Wrap onCommit from TaskBlocks so in-timeline moves never change the date.
  const onInTimelineCommit = useCallback((taskId, time, dur) => {
    onCommitTaskTime(taskId, time, dur);
  }, [onCommitTaskTime]);

  return (
    <div className="flex flex-col bg-card border border-slate-200 dark:border-white/10 rounded-xl shadow-sm overflow-hidden h-full" data-testid="day-timeline">
      {/* All-day events row */}
      <div data-ui="tl-allday" className="border-b border-slate-200 dark:border-white/10 px-3 py-1.5 bg-slate-50/60 dark:bg-white/[0.02] min-h-[32px]">
        <div className="flex items-center gap-1.5">
          <span data-ui="tl-alldaylabel" className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 font-medium w-12 flex-shrink-0">All-day</span>
          <div className="flex-1 flex flex-wrap gap-1 min-w-0">
            {allDayEvents.length === 0 && (
              <span className="text-[11px] text-slate-300 dark:text-slate-600">—</span>
            )}
            {allDayEvents.map((ev) => (
              <a
                key={ev.id}
                href={ev.html_link || undefined}
                target={ev.html_link ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="text-[10px] px-1.5 py-0.5 rounded font-medium truncate max-w-full"
                style={{ backgroundColor: `${ev.color || '#3b82f6'}22`, color: ev.color || '#3b82f6' }}
                title={ev.title}
                data-testid={`timeline-allday-${ev.id}`}
              >
                {ev.title}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Scrollable strip — all child positions are relative to this strip's top */}
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-y-auto overflow-x-hidden"
        style={{ scrollbarGutter: 'stable' }}
        onDragOver={onStripDragOver}
        onDragLeave={onStripDragLeave}
        onDrop={onStripDrop}
        data-timeline-strip
      >
        <div className="relative" style={{ height: TOTAL_PX + STRIP_BOTTOM_BUFFER }}>
          <HourGrid />

          {/* Events (z=10 via inline style on EventBlock) */}
          {visibleEventLayouts.map(({ item, lane, totalLanes }) => (
            <EventBlock
              key={item.event.id}
              event={item.event}
              top={topPxForMin(item.startMin)}
              height={(item.endMin - item.startMin) * PX_PER_MIN}
              lane={lane}
              totalLanes={totalLanes}
              clippedTop={item.clippedTop}
              clippedBottom={item.clippedBottom}
            />
          ))}

          {/* Tasks (z=20 via inline style on TaskBlock) */}
          {visibleTaskLayouts.map(({ item, lane, totalLanes }) => (
            <TaskBlock
              key={item.task.id}
              task={item.task}
              startMin={item.startMin}
              durationMin={item.durationMin}
              lane={lane}
              totalLanes={totalLanes}
              onToggleComplete={onToggleComplete}
              onEdit={onEditTask}
              onCommit={onInTimelineCommit}
            />
          ))}

          {/* Drag-from-list preview ghost */}
          {dragOver && (
            <div
              className="absolute pointer-events-none rounded-md border-2 border-dashed border-indigo-400 bg-indigo-100/40 dark:bg-indigo-500/15"
              style={{
                top: dragOver.yPx,
                height: DEFAULT_DURATION * PX_PER_MIN,
                left: LABEL_COL_PX + 6,
                right: 8,
                zIndex: 25,
              }}
              data-testid="timeline-drop-ghost"
            >
              <div className="px-2 py-1 text-[11px] font-medium text-indigo-700 dark:text-indigo-200">
                {formatTime12h(minutesToTimeString(dragOver.snappedMin))}
              </div>
            </div>
          )}

          {/* Now indicator */}
          <NowIndicator now={now} isToday={isToday} />
        </div>
      </div>
    </div>
  );
}

export default DayTimeline;
